import { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { streamChat } from '../../services/api';

interface VideoRecommendation {
  lessonId: string;
  trainingId: string;
  reason: string;
  raw: string;
}

function parseVideoRecommendations(text: string): { text: string; recommendations: VideoRecommendation[] } {
  const recommendations: VideoRecommendation[] = [];
  const regex = /\[VIDEO:([^|]+)\|([^|]+)\|([^\]]+)\]/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    recommendations.push({
      lessonId: match[1].trim(),
      trainingId: match[2].trim(),
      reason: match[3].trim(),
      raw: match[0],
    });
  }
  const cleanedText = text.replace(regex, '').trim();
  return { text: cleanedText, recommendations };
}

function VideoRecommendationCard({ rec, onClick }: { rec: VideoRecommendation; onClick: () => void }) {
  return (
    <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mt-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl primary-gradient flex items-center justify-center shrink-0">
          <span className="material-symbols-outlined text-on-primary text-lg">play_circle</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-primary uppercase tracking-widest mb-1">Featured Video</p>
          <p className="text-sm text-on-surface-variant leading-relaxed">{rec.reason}</p>
          <button
            onClick={onClick}
            className="mt-2 flex items-center gap-1 text-xs font-bold text-primary hover:underline"
          >
            Watch now <span className="material-symbols-outlined text-xs">arrow_forward</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function MarkdownContent({ content }: { content: string }) {
  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => <p className="text-sm mb-1 last:mb-0">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold text-on-surface">{children}</strong>,
        ul: ({ children }) => <ul className="list-disc list-inside space-y-1 my-1">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 my-1">{children}</ol>,
        li: ({ children }) => <li className="text-sm marker:text-primary">{children}</li>,
        h1: ({ children }) => <h1 className="text-base font-bold text-on-surface mt-3 mb-1">{children}</h1>,
        h2: ({ children }) => <h2 className="text-sm font-bold text-on-surface mt-3 mb-1">{children}</h2>,
        h3: ({ children }) => <h3 className="text-sm font-semibold text-on-surface mt-2 mb-1">{children}</h3>,
        code: ({ className, children, ...props }) => {
          const isInline = !className;
          if (isInline) {
            return (
              <code className="bg-surface-container-high px-1.5 py-0.5 rounded text-xs font-mono text-on-surface" {...props}>
                {children}
              </code>
            );
          }
          return (
            <pre className="bg-surface-container-high rounded-lg p-3 overflow-x-auto my-2">
              <code className="text-xs font-mono text-on-surface" {...props}>{children}</code>
            </pre>
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

export interface AskMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  recommendations?: VideoRecommendation[];
}

export function AskAiPanel({
  lessonId,
  onNavigateToVideo,
  messages: externalMessages,
  onMessagesChange,
}: {
  lessonId: string;
  onNavigateToVideo: (trainingId: string, lessonId: string) => void;
  messages?: AskMessage[];
  onMessagesChange?: (messages: AskMessage[]) => void;
}) {
  const [internalMessages, setInternalMessages] = useState<AskMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isControlled = externalMessages !== undefined;
  const messages = isControlled ? externalMessages : internalMessages;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const updateMessages = useCallback((updater: (prev: AskMessage[]) => AskMessage[]) => {
    const next = updater(messagesRef.current);
    if (isControlled) {
      onMessagesChange!(next);
    } else {
      setInternalMessages(next);
    }
  }, [isControlled, onMessagesChange]);

  useEffect(() => {
    if (!isControlled) setInternalMessages([]);
  }, [lessonId, isControlled]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const sendMessage = useCallback(async () => {
    const content = input.trim();
    if (!content || isStreaming) return;

    const userMsg: AskMessage = { id: crypto.randomUUID(), role: 'user', content };
    const assistantMsgId = crypto.randomUUID();
    const assistantMsg: AskMessage = { id: assistantMsgId, role: 'assistant', content: '', isStreaming: true };

    updateMessages((prev) => [...prev, userMsg, assistantMsg]);
    setInput('');
    setIsStreaming(true);

    const history = messagesRef.current
      .filter((m) => !m.isStreaming)
      .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));

    // Accumulate streamed tokens in a local variable rather than reading prior
    // content off (possibly stale) React state. SSE chunks arrive faster than
    // React re-renders, so a relative `m.content + chunk` append races and drops
    // tokens — producing scrambled/fragmented output. Setting the absolute
    // accumulated value each chunk is order-safe regardless of render timing.
    let accumulated = '';

    await streamChat(
      content,
      history,
      (chunk) => {
        accumulated += chunk;
        updateMessages((prev) =>
          prev.map((m) => (m.id === assistantMsgId ? { ...m, content: accumulated } : m)),
        );
      },
      () => {
        updateMessages((prev) => {
          const updated = prev.map((m) => {
            if (m.id !== assistantMsgId) return m;
            const parsed = parseVideoRecommendations(m.content);
            return { ...m, content: parsed.text, isStreaming: false, recommendations: parsed.recommendations };
          });
          return updated;
        });
        setIsStreaming(false);
      },
      (err) => {
        updateMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId ? { ...m, content: `Error: ${err}`, isStreaming: false } : m,
          ),
        );
        setIsStreaming(false);
      },
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      lessonId,
    );
  }, [input, isStreaming, updateMessages, lessonId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="bg-surface-container-low rounded-xl border border-outline-variant/10 flex flex-col h-[500px]">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isEmpty && (
          <div className="text-center py-8">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-3">
              <span className="material-symbols-outlined text-2xl">auto_awesome</span>
            </div>
            <h4 className="text-sm font-bold text-on-surface mb-1">Ask AI about this video</h4>
            <p className="text-xs text-on-surface-variant max-w-sm mx-auto">
              Ask anything about the content — concepts explained, clarifications, or how to apply what you learned.
            </p>
          </div>
        )}

        {messages.map((msg) => {
          if (msg.role === 'user') {
            return (
              <div key={msg.id} className="flex gap-2 justify-end">
                <div className="bg-surface-container-high px-4 py-2.5 rounded-xl rounded-tr-none text-on-surface text-sm leading-relaxed max-w-[80%]">
                  {msg.content}
                </div>
              </div>
            );
          }

          return (
            <div key={msg.id} className="flex gap-2">
              <div className="w-7 h-7 rounded-full primary-gradient flex items-center justify-center shrink-0 mt-0.5">
                <span className="material-symbols-outlined text-on-primary text-xs">auto_awesome</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="bg-surface-container px-4 py-3 rounded-xl rounded-tl-none text-on-surface text-sm leading-relaxed border border-outline-variant/5">
                  {msg.content ? (
                    <MarkdownContent content={msg.content} />
                  ) : msg.isStreaming ? (
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 bg-on-surface-variant rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-on-surface-variant rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-on-surface-variant rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                  ) : null}
                </div>
                {msg.recommendations?.map((rec) => (
                  <VideoRecommendationCard
                    key={rec.lessonId}
                    rec={rec}
                    onClick={() => onNavigateToVideo(rec.trainingId, rec.lessonId)}
                  />
                ))}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-outline-variant/10">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this video..."
            rows={1}
            className="flex-1 bg-surface-container rounded-xl px-4 py-2.5 text-sm text-on-surface placeholder:text-on-surface-variant/50 border border-outline-variant/20 focus:border-primary/40 focus:outline-none transition-colors resize-none max-h-24"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isStreaming}
            className="p-2.5 rounded-xl primary-gradient text-on-primary disabled:opacity-40 transition-all"
          >
            <span className="material-symbols-outlined text-lg">send</span>
          </button>
        </div>
      </div>
    </div>
  );
}
